import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { TimeDurationInputComponent } from 'src/app/controls/time-duration-input/time-duration-input.component';
import { RichToolTipDirective } from 'src/app/directives/rich-tool-tip.directive';
import { WorkflowActionTypes } from 'src/app/models/RestApiCaller/Enums/workflow-action-types';
import { WorkflowTriggerTypes } from 'src/app/models/RestApiCaller/Enums/workflow-trigger-types';
import { NotificationService } from 'src/app/services/notification-service';
import { ActivateStepActionData } from '../../../models/activate-step-action-data';
import { ChangeStepStateActionData } from '../../../models/change-step-state-action-data';
import { WorkflowConditionTypes } from '../../../models/Enums/workflow-condition-types';
import { ExpiredDelayTriggerData } from '../../../models/expire-delay-trigger-data';
import { State } from '../../../models/state';
import { StepData } from '../../../models/step-data';
import { StepSpecificStateConditionData } from '../../../models/step-specific-state-condition-datas';
import { StepStateChangedTriggerData } from '../../../models/step-state-change-trigger-data';
import { TimerTriggerData } from '../../../models/timer-trigger-data';
import { Workflow } from '../../../models/workflow';
import { IFormComponent } from '../core/form-component.interface';
import { StepStateControl } from './controls/step-state-input-dialog/step-states.control';

@Component({
    selector: 'app-workflow-step-form',
    templateUrl: './workflow-step-form.component.html',
    styleUrl: './workflow-step-form.component.scss',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        FaIconComponent,
        CommonModule,
        StepStateControl,
        MatTooltipModule,
        RichToolTipDirective,
        TimeDurationInputComponent,
    ],
})
export class WorkflowStepFormComponent implements IFormComponent, OnInit {
    /**
     * Liste des icones utilisés dans le template.
     */
    public faCircleInfo = faCircleInfo;

    public maxDurationTooltipContent =
        "Spécifie la durée maximale de traitement d'un dossier pour cette étape de workflow. Une étape dont l'activation est antérieure à la date du jour moins cette durée est considérée comme en retard.<br/><br/><b>Utilisez le format : </b>2s 4j 6h 45m<br><ul><li>s = semaines</li><li>j = jours</li><li>h = heures</li><li>m = minutes</li></ul>";

    /**
     * Données de l'étape affichées dans le formulaire.
     */
    private _data: StepData;

    /**
     * Spécifie les données de l'étape affichées dans le formulaire.
     */
    set data(value: StepData) {
        this._data = value;
        this.fillForm();
    }

    /**
     * Obtient les données de l'étape affichées dans le formulaire.
     */
    get data(): StepData {
        this.fillData();
        return this._data;
    }

    /**
     * Instance du workflow.
     */
    public workflow: Workflow;

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    get isDirty(): boolean {
        return this.dataForm && this.dataForm.dirty;
    }

    /**
     * Constructeur.
     * @param changeDetector : Detecteur de changement.
     */
    constructor(
        private changeDetector: ChangeDetectorRef,
        private notificationService: NotificationService
    ) {
        console.log('WorkflowStepFormComponent - constructor : ');
    }

    /**
     * Initialise le composant.
     */
    ngOnInit() {
        this.dataForm = new UntypedFormGroup({
            id: new UntypedFormControl({
                value: '',
                disabled: true,
            }),
            stepName: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
            maxDuration: new UntypedFormControl({ value: '', disabled: false }),
            states: new UntypedFormControl({ value: [] }, [Validators.required]),
        });

        this.fillForm();
    }

    /**
     * Remplit le formulaire avec les données de l'étape.
     */
    private fillForm() {
        if (this._data) {
            if (this.dataForm) {
                this.dataForm.get('id').setValue(this._data.id);
                this.dataForm.get('stepName').setValue(this._data.stepName);
                this.dataForm.get('maxDuration').setValue(this._data.maxDuration);
                this.dataForm.get('states').setValue(this._data.states);
            }
        }
    }

    /**
     * Récupère les données à partir des champs du formulaire et met à jour l'object data.
     */
    public fillData() {
        let stepData: StepData = new StepData();

        Object.keys(this.dataForm.controls).forEach((key) => {
            stepData[key] = this.dataForm.get(key).value;
        });

        this.data = stepData;

        this.dataForm.markAsPristine();

        console.log('Fill data', this._data);
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.dataForm.markAsPristine();
    }

    /**
     * Appelée pour déterminer si la suppression d'un état est permise.
     * @param state : Instance de l'état à supprimer.
     * @returns True si la suppression de l'état est permise; false autrement.
     */
    public isStateDeletionAllowed(state: State): boolean {
        return (
            this.checkStepStateUseByAnyTrigger(this._data.id, state) === false &&
            this.checkStepStateUseByAnyAction(this._data.id, state) === false &&
            this.checkStepStateUseByAnyEventCondition(this._data.id, state) === false
        );
    }

    /**
     * Vérifie l'utilisation d'un état par des triggers du workflow.
     * @param state : Instance de l'état à vérifier.
     * @returns True si au moins un trigger utilise l'état.
     */
    private checkStepStateUseByAnyTrigger(stepId: number, state: State): boolean {
        for (let trigger of this.workflow.triggers) {
            if (trigger.type === WorkflowTriggerTypes.DelayExpired) {
                let expiredDelayTriggerData = trigger.data as ExpiredDelayTriggerData;

                if (expiredDelayTriggerData.stepId === stepId && expiredDelayTriggerData.stateId === state.id) {
                    this.notificationService.displayWarning(
                        'Suppression impossible',
                        `La suppression de l'état '${state.name}' n'est pas permise car il est referencé par le trigger ${trigger.id} de type 'Délai expiré'.`
                    );
                    return true;
                }
            }

            if (trigger.type === WorkflowTriggerTypes.Timer) {
                let timerTriggerData = trigger.data as TimerTriggerData;

                if (timerTriggerData.stepId === stepId && timerTriggerData.stateId === state.id) {
                    this.notificationService.displayWarning(
                        'Suppression impossible',
                        `La suppression de l'état '${state.name}' n'est pas permise car il est referencé par le minuteur ${trigger.id}.`
                    );
                    return true;
                }
            }

            if (trigger.type === WorkflowTriggerTypes.StepStateChanged) {
                let stepStateChangedTriggerData = trigger.data as StepStateChangedTriggerData;

                if (stepStateChangedTriggerData.stepId === stepId && stepStateChangedTriggerData.stateId === state.id) {
                    this.notificationService.displayWarning(
                        'Suppression impossible',
                        `La suppression de l'état '${state.name}' n'est pas permise car il est referencé par un trigger de changement d'état.`
                    );
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Vérifie l'utilisation d'un état par des triggers du workflow.
     * @param state : Instance de l'état à vérifier.
     * @returns True si au moins un trigger utilise l'état.
     */
    private checkStepStateUseByAnyAction(stepId: number, state: State): boolean {
        for (let action of this.workflow.actions) {
            if (action.type === WorkflowActionTypes.ChangeStepState) {
                let changeStepStateActionData = action.data as ChangeStepStateActionData;

                if (changeStepStateActionData.stepId === stepId && changeStepStateActionData.stateId === state.id) {
                    this.notificationService.displayWarning(
                        'Suppression impossible',
                        `La suppression de l'état '${state.name}' n'est pas permise car il est referencé par une action de changement d'état.`
                    );
                    return true;
                }
            }

            if (action.type === WorkflowActionTypes.ActivateStep) {
                let activateStepActionData = action.data as ActivateStepActionData;

                if (activateStepActionData.stepId === stepId && activateStepActionData.stateId === state.id) {
                    this.notificationService.displayWarning(
                        'Suppression impossible',
                        `La suppression de l'état '${state.name}' n'est pas permise car il est referencé par une action d'activation d'étape.`
                    );
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Vérifie l'utilisation d'un état par des conditions d'évènement du workflow.
     * @param state : Instance de l'état à vérifier.
     * @returns True si au moins une condition d'évènement utilise l'état.
     */
    private checkStepStateUseByAnyEventCondition(stepId: number, state: State): boolean {
        for (let events of this.workflow.events) {
            for (let condition of events.data?.conditions.filter((x) => x.type === WorkflowConditionTypes.StepSpecificState)) {
                let specificStateCondition = condition as StepSpecificStateConditionData;
                if (specificStateCondition.stateId === state.id) {
                    this.notificationService.displayWarning(
                        'Suppression impossible',
                        `La suppression de l'état '${state.name}' n'est pas permise car il est referencé par une condition d'évènement de changement d'état.`
                    );
                    return true;
                }
            }
        }

        return false;
    }
}
